import { StoreKey, type Locale } from "src/lib/Intl";
import type { SupportedKeys } from "src/types/Carousels";
import { PromotionType } from "src/types/graphql.d";
import type { ProductListType } from "src/types/product-listing";

export const HEADER_STICKY_OFFSET = 64;
export const HEADER_STICKY_OFFSET_MOBILE = 58;
// PDP breadcrumb bar: 16px wrapper padding + 8px container padding + 20px item.
export const PDP_BREADCRUMB_HEIGHT = 44;
export const BODY_NO_SCROLL_CLASS = "no-scroll";

export const SEARCH_ANIMATION_TIME = 700;

export const DEFAULT_REVALIDATION: number = 60 * 15;
export const DEFAULT_PAGE_NOT_FOUND = {
  notFound: true,
  revalidate: 60,
} as const;

export const DEFAULT_PRODUCTS_PER_PAGE = 72;

export const OT_SHOP_TYPE_COOKIE = "ot-shop-type";
export const OT_APP_COOKIE = "ot-app";
export const OT_SCROLL_POSITION = "ot_scroll_position";
export const OT_AUTH_TOKEN_COOKIE = "ot_auth_token";
export const OT_ANONYMOUS_ID = "ot_anonymous_id";
// Legacy guest-cart key (held cart.anonymousId). No longer written; removed on read.
export const OT_GUEST_CART_ID = "ot_guest_cart_id";
// Guest session hand-over (localStorage): CT cart id of a guest cart with line items,
// the guest session's CT anonymousId, and how many merge attempts have failed.
export const OT_GUEST_CART_REF = "ot_guest_cart_ref";
export const OT_GUEST_ANONYMOUS_ID = "ot_guest_anonymous_id";
export const OT_GUEST_MERGE_ATTEMPTS = "ot_guest_merge_attempts";
export const OT_CHECKOUT_TRACKING_DATA = "ot_checkout_tracking_data";
export const OT_CHECKOUT_CUSTOMER = "ot_checkout_customer";
export const OT_STORE_CREDIT_SEEN = "ot_store_credit_seen";
export const OT_GUEST_LOOKUP_ATTEMPTS = "ot_guest_lookup_attempts";

// Set by the Constructor.io beacon script (see _app.tsx) in localStorage — per Constructor's
// own guidance, not the ConstructorioID_client_id/_session_id cookies (those turned out to be
// unreliable: host-only scoped, so they don't carry across sibling QA subdomains, among other
// issues). Read explicitly and sent as the constructor_tracking_data GraphQL variable on
// carousel queries (see usePdpCarousels, useCartCarousels, useNewInHomepage).
export const CONSTRUCTOR_CLIENT_ID_STORAGE_KEY =
  "_constructorio_search_client_id";
export const CONSTRUCTOR_SESSION_ID_STORAGE_KEY =
  "_constructorio_search_session_id";

export const SIGN_IN_PAGES: readonly string[] = [
  "/sign-in",
  "/register",
  "/register-email",
];

export const SKIP_SIGN_IN_MESSAGE: Readonly<Record<string, string>> = {
  WITH_TRACKING: "skip-sign-in-with-tracking",
  WITHOUT_TRACKING: "skip-sign-in-without-tracking",
} as const;

export const HOME_PAGE_SLUGS: Readonly<Record<StoreKey, readonly string[]>> = {
  "OT-COM": ["women", "men", "kids"],
  "OT-UK": ["women", "men", "kids"],
  "OT-DE": ["damen", "herren", "kinder"],
  "OT-BE": ["dames", "heren", "kids"],
  "OT-NL": ["dames", "heren", "kids"],
  "OT-ES": ["mujer", "hombre", "ninos"],
  "OT-FR": ["femme", "homme", "enfant"],
  "OT-IT": ["donna", "uomo", "bambini"],
  "pl-PL": ["kobiety", "mezczyzni", "dzieci"],
  "OT-ZZ": [],
  "OT-US": [],
  "": [],
} as const;

export const PROMOTION_TYPE = {
  SPOTLIGHT_BRANDS: "spotlight_brands",
  FEATURED_SHOP: "featured_shop",
  INSPIRATIONAL_SECTIONS: "inspirational_sections",
  DESIGNER_BRANDS_SECTIONS: "design-brands_sections",
  MEET_BRAND: "meet_brand",
  TOP_ALERT_BAR: "top_alert_bar",
  ACTION_BLOCK: "action_block",
  PLP_PROMO_BANNER: "plp_promo_banner",
  TOASTER_NOTIFICATION: "toaster_notifications",
  WISHLIST_BRAND_COLLECTION: "wishlist_brand_collection",
  POPULAR_BRANDS: "popular_brands",
  JUST_ARRIVED_SHOPS: "just_arrived_shops",
  POPULAR_SHOPS: "popular_shops",
  FAVOURITED_SHOPS: "favourited_shops",
  DEPARTMENT_DESIGNER: "designer_shops",
  CONSCIOUS_SHOPS: "conscious_shops",
  IMAGE_BANNERS: "image_banners",
  BRAND_CAROUSEL: "brands_carousel",
  PROMOTION_BANNER: "promotion_banner",
} as const;

export const CAROUSEL_CONTENT_TYPE = {
  CATEGORY: "category",
  BRAND: "brand",
  PRODUCT: "product",
  EDIT: "edit",
  IMAGE_BANNER: "image_banner",
} as const;

export const PDP_SIMILAR_ITEMS_CAROUSEL_ID = "pdp_similar_items_carousel";
export const POPUP_SIMILAR_ITEMS_CAROUSEL_ID = "pop_up_similar_items_carousel";
export const POPUP_SIMILAR_ITEMS_LIMIT = 3;
export const POPUP_AUTO_DISMISS_DELAY = 5000;

// carousel_id values for carousels whose id is not supplied by the backend.
// Values come from the "AT: Carousel ID" tracking plan and must not be renamed.
export const CAROUSEL_ID = {
  MEET_BRAND_PRODUCTS: "meet_brand_products",
  POPULAR_BRANDS_BIG: "popular_brands_big",
  FEATURED_SHOPS: "featured_shops",
} as const;

// Shared product card sizes
export const PRODUCT_CAROUSEL_SLIDES = {
  desktop: 6,
  tablet: 3.5,
  mobile: 1.99,
} as const;

export const PRODUCT_CAROUSEL_SLIDES_WITH_INFO_TEXT = {
  ...PRODUCT_CAROUSEL_SLIDES,
  desktop: 5,
} as const;

export const PRODUCT_CAROUSEL_SLIDE_WIDTH = {
  desktop: `${(100 / PRODUCT_CAROUSEL_SLIDES.desktop).toFixed(2)}%`,
  tablet: `${(100 / PRODUCT_CAROUSEL_SLIDES.tablet).toFixed(2)}%`,
  mobile: `${(100 / PRODUCT_CAROUSEL_SLIDES.mobile).toFixed(2)}%`,
} as const;

export const SUPPORTED_KEYS = [
  "just_arrived_shops",
  "popular_shops",
  "favourited_shops",
  "department_designer",
  "conscious_shops",
] as const;

export const SEGMENT_PROMOTION: Record<SupportedKeys, string> = {
  popular_shops: "popular_shops",
  favourited_shops: "favourited_shops",
  department_designer: "designer_shops",
  just_arrived_shops: "just_arrived_shops",
  conscious_shops: "conscious_shops",
};

export const PRODUCT_LIST_TYPE = {
  BRAND_PLP: "BRAND_PLP",
  CATEGORY_PLP: "CATEGORY_PLP",
  NEW_IN_PLP: "NEW_IN_PLP",
  HOT_DEALS_PLP: "HOT_DEALS_PLP",
  SALE_PLP: "SALE_PLP",
  DESIGNER_PLP: "DESIGNER_PLP",
  COLLECTIONS_PLP: "COLLECTIONS_PLP",
  SEARCH_PLP: "SEARCH_PLP",
  SHOP_TYPE_PLP: "SHOP_TYPE_PLP",
} as const;

export const LISTING_PAGE_NAME: Record<ProductListType, string> = {
  [PRODUCT_LIST_TYPE.BRAND_PLP]: "brand",
  [PRODUCT_LIST_TYPE.CATEGORY_PLP]: "category",
  [PRODUCT_LIST_TYPE.NEW_IN_PLP]: "new_in",
  [PRODUCT_LIST_TYPE.HOT_DEALS_PLP]: "hot_deals",
  [PRODUCT_LIST_TYPE.SALE_PLP]: "sale",
  [PRODUCT_LIST_TYPE.DESIGNER_PLP]: "designer",
  [PRODUCT_LIST_TYPE.COLLECTIONS_PLP]: "collection",
  [PRODUCT_LIST_TYPE.SEARCH_PLP]: "search_catalog",
  [PRODUCT_LIST_TYPE.SHOP_TYPE_PLP]: "shop_type",
} as const;

export const PROMOTION_TYPES: Partial<
  Record<ProductListType, PromotionType | null>
> = {
  BRAND_PLP: PromotionType.Brand,
  CATEGORY_PLP: PromotionType.Category,
} as const;

export const BRANDS_SLUG_TRANSLATION = [
  "marques",
  "marken",
  "merken",
  "marki",
  "marcas",
  "marchi",
  "brands",
];

export const BRAND_SLUG_LOCALE_TRANSLATION = {
  en: "brands",
  fr: "marques",
  de: "marken",
  nl: "merken",
  "nl-be": "merken",
  "it-it": "marchi",
  "es-es": "marcas",
  "pl-pl": "marki",
  "de-at": "marken",
  "en-gb": "brands",
  "en-dk": "brands",
  "en-se": "brands",
  "en-us": "brands",
} satisfies Record<Locale, string>;

const GOOGLE_PLAY_CAMPAIGN =
  "pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1";

const MOBILE_STORE_CODES = {
  en: { appStore: "gb", googlePlay: "en" },
  fr: { appStore: "fr", googlePlay: "fr" },
  de: { appStore: "de", googlePlay: "de" },
  nl: { appStore: "nl", googlePlay: "nl" },
  "nl-be": { appStore: "be", googlePlay: "nl" },
  "it-it": { appStore: "it", googlePlay: "it" },
  "es-es": { appStore: "es", googlePlay: "es" },
  "pl-pl": { appStore: "pl", googlePlay: "pl" },
  "de-at": { appStore: "at", googlePlay: "de" },
  "en-gb": { appStore: "gb", googlePlay: "en_GB" },
  "en-dk": { appStore: "dk", googlePlay: "en" },
  "en-se": { appStore: "se", googlePlay: "en" },
  "en-us": { appStore: "gb", googlePlay: "en" },
} satisfies Record<Locale, { appStore: string; googlePlay: string }>;

/** The Otrium app's id in Apple's App Store. */
export const IOS_APP_STORE_ID = "1465868643";

export const appStoreUrl = (locale: Locale): string =>
  `https://apps.apple.com/${MOBILE_STORE_CODES[locale].appStore}/app/id${IOS_APP_STORE_ID}`;

export const googlePlayUrl = (locale: Locale): string =>
  `https://play.google.com/store/apps/details?id=com.otrium.shop&hl=${MOBILE_STORE_CODES[locale].googlePlay}&pcampaignid=${GOOGLE_PLAY_CAMPAIGN}`;

export const GENDER_TRANSLATIONS: string[] = [
  "women",
  "femme",
  "damen",
  "dames",
  "kobiety",
  "mujer",
  "donna",
  "dame",
  "kvinna",
  "kids",
  "enfant",
  "kinder",
  "kids",
  "ninos",
  "bambini",
  "dzieci",
  "born",
  "barn",
  "men",
  "harren",
  "herren",
  "homme",
  "heren",
  "hombre",
  "uomo",
  "man",
  "mezczyzni",
];
